import React, { useEffect } from "react";
import { useFieldArray, Controller } from "react-hook-form";
import {
  Form,
  Collapse,
  Input,
  Select,
  Tooltip,
  Switch,
  AutoComplete,
  Space,
  Button,
  message,
  Checkbox,
} from "antd";

import {
  MinusCircleOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import cronChecker from "cron-validate";
import { formItemLayout } from "../../../utils/UI";
import { HMGSelect } from "../../Common/DataAttachedElements/index";

const { Panel } = Collapse;
const { Option } = Select;

const timezones = [
  { label: "AKST", value: "America/Anchorage" },
  { label: "AST", value: "America/Halifax" },
  { label: "CST", value: "America/Chicago" },
  { label: "EST", value: "America/New_York" },
  { label: "HST", value: "Pacific/Honolulu" },
  { label: "MT", value: "America/Phoenix" },
  { label: "MDT", value: "America/Denver" },
  { label: "NST", value: "America/St_Johns" },
  { label: "PST", value: "America/Los_Angeles" },
  { label: "SL", value: "Asia/Colombo" },
];

const JobsConfigurationElement = (props) => {
  const {
    control,
    errors,
    SFTPJobData,
    isAddMode,
    setError,
    watch,
    onEditJobValues,
    isCloneMode,
    isLoadingSFTPData,
    setValue,
    HMGProjectData,
    projectHotels,
    clearErrors,
    handleCloneJobCheck,
  } = props;

  const { fields, append, remove } = useFieldArray({
    control,
    errors,
    name: "job.schedules",
  });

  // when clone or edit mode upper value change according to rename value
  useEffect(() => {
    if (watch("job.jobName1") !== undefined) {
      setValue("job.jobName", watch("job.jobName1"));
    }
  }, [watch("job.jobName1")]);

  // to check cron valid or not
  const cloneChecker = (e) => {
    const cronFelidArray = watch("job.schedules");
    let cronResult;
    let cronFelid;

    for (let index = 0; index < cronFelidArray.length; index += 1) {
      cronFelid = cronFelidArray[index].cron.substring(
        cronFelidArray[index].cron.lastIndexOf("cron(") + 5,
        cronFelidArray[index].cron.lastIndexOf(")"),
      );
      cronResult = cronChecker(cronFelid, {
        preset: "aws-cloud-watch",
      });

      if (cronResult.isError()) {
        setError(
          `job.schedules.${index}.cron`,
          {
            type: "focus",
            message: "Please Use Following Format - ex:-cron(0 12 * * ? *)",
          },
          { shouldFocus: true },
        );
        message.error("Please Use Following Format - ex:-cron(0 12 * * ? *)");

        return true;
      }
      clearErrors();
      return false;
    }
  };

  return (
    <>
      <div className="container_ab">
        <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
          <Panel header="Job Configuration " key="1">
            <Form.Item
              {...formItemLayout}
              help={errors?.job?.jobName?.message}
              required
              validateStatus={errors?.job?.jobName ? "error" : ""}
              label="Job Name"
            >
              <Controller
                control={control}
                name="job.jobName"
                render={({ field: { onChange, value } }) => (
                  <AutoComplete
                    showSearch
                    loading={isLoadingSFTPData}
                    onChange={(e, val) => {
                      onChange(e, val);
                      onEditJobValues(e, val);
                    }}
                    value={value}
                    allowClear
                    filterOption={(input, option) => {
                      return (
                        option.value
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) !== -1
                      );
                    }}
                    placeholder="Job Name"
                  >
                    {SFTPJobData !== undefined
                      ? SFTPJobData?.map((elem) => {
                          return (
                            <Option key={elem._id} value={elem.jobName}>
                              {elem.jobName}
                            </Option>
                          );
                        })
                      : undefined}
                  </AutoComplete>
                )}
              />
            </Form.Item>

            <Form.Item {...formItemLayout} label="Clone Job">
              <>
                <Checkbox
                  checked={isCloneMode}
                  onChange={async (e) => {
                    await handleCloneJobCheck(e);
                  }}
                  disabled={isAddMode === true}
                />
                <Tooltip placement="topLeft" title="Clone Job" arrowPointAtLeft>
                  <ExclamationCircleOutlined
                    style={{ color: "red", marginLeft: 10 }}
                  />
                </Tooltip>
              </>
            </Form.Item>
            <div
              className="is_animation_1"
              style={
                isAddMode === true ? { maxHeight: "0" } : { maxHeight: "100px" }
              }
            >
              <Form.Item
                {...formItemLayout}
                help={errors?.job?.jobName1?.message}
                required
                validateStatus={errors?.job?.jobName1 ? "error" : ""}
                label="Rename Job Name"
              >
                <Controller
                  control={control}
                  name="job.jobName1"
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Rename Job Name"
                      suffix={
                        <>
                          <Tooltip
                            placement="topLeft"
                            title="Please Don`t Add Spaces "
                            arrowPointAtLeft
                          >
                            <ExclamationCircleOutlined
                              style={{ color: "red" }}
                            />
                          </Tooltip>
                        </>
                      }
                    />
                  )}
                />
              </Form.Item>
            </div>

            <div
              className="is_animation_1"
              style={
                isCloneMode === false
                  ? { maxHeight: "0" }
                  : { maxHeight: "100px" }
              }
            >
              <Form.Item
                {...formItemLayout}
                help={errors?.job?.jobName1?.message}
                required
                validateStatus={errors?.job?.jobName1 ? "error" : ""}
                label="Clone Job Name"
              >
                <Controller
                  control={control}
                  name="job.jobName1"
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Clone Job Name"
                      suffix={
                        <>
                          <Tooltip
                            placement="topLeft"
                            title="Please Don`t Add Spaces "
                            arrowPointAtLeft
                          >
                            <ExclamationCircleOutlined
                              style={{ color: "red" }}
                            />
                          </Tooltip>
                        </>
                      }
                    />
                  )}
                />
              </Form.Item>
            </div>

            <Form.Item
              {...formItemLayout}
              label="Hotel Management Group"
              help={errors?.job?.managemetGroupId?.message}
              required
              validateStatus={errors?.job?.managemetGroupId ? "error" : ""}
            >
              <Controller
                control={control}
                name="job.managemetGroupId"
                render={({ field: { onChange, value } }) => (
                  <HMGSelect value={value} onChange={onChange} allowClear />
                )}
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              label="Project"
              help={errors?.job?.projectOId?.message}
              required
              validateStatus={errors?.job?.projectOId ? "error" : ""}
            >
              <Controller
                control={control}
                name="job.projectOId"
                render={({ field: { onChange, value } }) => (
                  <Select
                    showSearch
                    onChange={onChange}
                    value={value}
                    allowClear
                    filterOption={(input, option) => {
                      return (
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) !== -1
                      );
                    }}
                    placeholder="Project"
                  >
                    {HMGProjectData?.map((elem) => {
                      return (
                        <Option key={elem._id} value={elem._id}>
                          {elem.project_name}
                        </Option>
                      );
                    })}
                  </Select>
                )}
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              label="Hotel"
              help={errors?.job?.hotelId?.message}
              validateStatus={errors?.job?.hotelId ? "error" : ""}
            >
              <Controller
                control={control}
                name="job.hotelId"
                render={({ field: { onChange, value } }) => (
                  <Select
                    showSearch
                    onChange={onChange}
                    value={value}
                    allowClear
                    filterOption={(input, option) => {
                      return (
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) !== -1
                      );
                    }}
                    placeholder="Hotel"
                  >
                    {projectHotels?.map((elem) => {
                      return (
                        <Option key={elem._id} value={elem._id}>
                          {elem.hotel_name}
                        </Option>
                      );
                    })}
                  </Select>
                )}
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              help={errors?.job?.timezone?.message}
              required
              validateStatus={errors?.job?.timezone ? "error" : ""}
              label="Time Zone"
            >
              <Controller
                control={control}
                name="job.timezone"
                render={({ field }) => (
                  <Select
                    {...field}
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      return (
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) !== -1
                      );
                    }}
                    placeholder="Time Zone"
                  >
                    {timezones?.map((elem) => {
                      return (
                        <Option key={elem.label} value={elem.value}>
                          {elem.label}
                        </Option>
                      );
                    })}
                  </Select>
                )}
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              label="Schedules"
              help={errors?.job?.schedules?.message}
              required
              validateStatus={errors?.job?.schedules ? "error" : ""}
              name="job.schedules"
            >
              {fields.map((item, index) => {
                return (
                  <div>
                    <Space
                      key={item._id}
                      style={{ display: "Flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        label="Cron"
                        help={errors.job?.schedules?.[index]?.cron?.message}
                        required
                        validateStatus={
                          errors.job?.schedules?.[index]?.cron ? "error" : ""
                        }
                      >
                        <Controller
                          control={control}
                          name={`job.schedules.${index}.cron`}
                          render={({ field: { onChange, value } }) => (
                            <Input
                              placeholder="Cron"
                              onChange={(e) => {
                                onChange(e);
                                cloneChecker(e);
                              }}
                              value={value}
                            />
                          )}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Description"
                        help={
                          errors.job?.schedules?.[index]?.description?.message
                        }
                        required
                        validateStatus={
                          errors.job?.schedules?.[index]?.description
                            ? "error"
                            : ""
                        }
                      >
                        <Controller
                          control={control}
                          name={`job.schedules.${index}.description`}
                          render={({ field }) => (
                            <Input {...field} placeholder="Description" />
                          )}
                        />
                      </Form.Item>

                      <MinusCircleOutlined onClick={() => remove(index)} />
                    </Space>
                  </div>
                );
              })}
              <Button
                disabled={watch("job.schedules")?.length === 1}
                block
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => append({ cron: "", description: "" })}
              >
                Add Schedules
              </Button>
            </Form.Item>
            <Form.Item {...formItemLayout} label="Enabled">
              <Controller
                control={control}
                name="job.enabled"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(value) => {
                      onChange(value);
                    }}
                  />
                )}
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              help={errors?.job?.processType?.message}
              required
              validateStatus={errors?.job?.processType ? "error" : ""}
              label="Process Type"
            >
              <Controller
                control={control}
                name="job.processType"
                render={({ field }) => (
                  <Select showSearch {...field} placeholder="Process Type">
                    <option value="Standard">Standard</option>
                  </Select>
                )}
              />
            </Form.Item>
            <Form.Item {...formItemLayout} label="Delete Files From Source">
              <Controller
                control={control}
                name="job.deleteFilesFromSource"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(value) => {
                      onChange(value);
                    }}
                  />
                )}
              />
            </Form.Item>
            <Form.Item {...formItemLayout} label="Ignore Previous Status">
              <Controller
                control={control}
                name="job.ignorePreviousStatus"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(values) => {
                      onChange(values);
                    }}
                  />
                )}
              />
            </Form.Item>
          </Panel>
        </Collapse>
      </div>
    </>
  );
};

export default JobsConfigurationElement;
